import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import BodyType from '../../../../../../types/body-type/BodyType';
import Brand from '../../../../../../types/brand/Brand';
import CarClass from '../../../../../../types/car-class/CarClass';
import Media from '../../../../../../types/media/Media';
import VehicleModelWriteDTO from '../../../../../../types/vehicle-model/VehicleModelWriteDTO';
import { BodyTypeService } from '../../../../services/body-type.service';
import { BrandService } from '../../../../services/brand.service';
import { CarClassService } from '../../../../services/car-class.service';
import { UtilsService } from '../../../../services/utils.service';
import { VehicleModelService } from '../../../../services/vehicle-model.service';
import {
    SelectOrUploadMediaComponent
} from '../../media/select-or-upload-media-dialog/select-or-upload-media.component';

@Component({
    selector: 'app-create-vehicle-model-dialog',
    templateUrl: './create-vehicle-model-dialog.component.html',
    styleUrls: [ './create-vehicle-model-dialog.component.scss' ],
    imports: [
        DialogModule,
        InputTextModule,
        ButtonModule,
        FormsModule,
        IconField,
        InputIcon,
        IftaLabelModule,
        Select,
        DatePicker,
        SelectOrUploadMediaComponent,
    ]
})
export class CreateVehicleModelDialogComponent implements OnInit {
    @Input() visible: boolean = false;
    @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() vehicleModelCreated: EventEmitter<VehicleModelWriteDTO> = new EventEmitter<VehicleModelWriteDTO>();

    public vehicleModel: VehicleModelWriteDTO = new VehicleModelWriteDTO({});
    public loading = false;

    public brands: Brand[] = [];
    public bodyTypes: BodyType[] = [];
    public carClasses: CarClass[] = [];

    // Dates
    public availabilityDate: Date | undefined;
    public expirationDate: Date | undefined

    public media: Media | undefined = undefined;

    constructor(
        private vehicleModelService: VehicleModelService,
        private brandService: BrandService,
        private bodyTypeService: BodyTypeService,
        private carClassService: CarClassService,
        private messageService: MessageService,
        private utilsService: UtilsService
    ) {
    }

    ngOnInit() {
        this.loadBrands();
        this.loadBodyTypes();
        this.loadCarClasses();
    }

    private loadBrands() {
        this.brandService.getAllBrands().then((brands) => {
            this.brands = brands;
        });
    }

    private loadBodyTypes() {
        this.bodyTypeService.getAllBodyTypes().then((bodyTypes) => {
            this.bodyTypes = bodyTypes;
        });
    }

    private loadCarClasses() {
        this.carClassService.getAllCarClasses().then((carClasses) => {
            this.carClasses = carClasses;
        });
    }

    protected onCarClassOrBodyTypeChange() {
        if (this.vehicleModel.carClassId && this.vehicleModel.bodyTypeId) {
            const carClassName = this.carClasses.find(
                (carClass) => carClass.id === this.vehicleModel.carClassId)?.label;
            const bodyTypeName = this.bodyTypes.find((bodyType) => bodyType.id === this.vehicleModel.bodyTypeId)?.label;

            this.vehicleModel.label = `${ carClassName } ${ bodyTypeName }`;
        }
    }

    get saveDisabled(): boolean {
        return (
            !this.vehicleModel.modelSeries ||
            !this.vehicleModel.label ||
            !this.vehicleModel.brandId ||
            !this.vehicleModel.bodyTypeId ||
            !this.vehicleModel.carClassId
        );
    }

    public close() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
    }

    public saveVehicleModel() {
        this.loading = true;

        // Convert date objects to ISO strings
        this.vehicleModel.availabilityDate = this.utilsService.convertDateToDateString(this.availabilityDate);
        this.vehicleModel.expirationDate = this.utilsService.convertDateToDateString(this.expirationDate);

        if (this.media) {
            this.vehicleModel.imageId = this.media.id;
        }

        this.vehicleModelService
            .createVehicleModel(this.vehicleModel)
            .then(() => {
                this.vehicleModelCreated.emit(this.vehicleModel);
                this.close();
            })
            .catch((error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000,
                });
            })
            .finally(() => {
                this.vehicleModel = new VehicleModelWriteDTO({});
                this.loading = false;
            });
    }

}
